import { z } from 'zod';
import { emailSchema, passwordSchema, loginPasswordSchema } from '@/lib/validation';
import { stripPhoneDigits, isCompleteNanpPhone } from '@/lib/phone';
import { nameAnswerSchema } from './survey';

export const loginSchema = z.object({
  email: emailSchema,
  password: loginPasswordSchema.optional(),
});

export const otpSchema = z.object({
  code: z.string().length(6, 'Please enter the 6-digit code'),
});

// REK-1986 (SSO.5): phone-login tab. The field holds the DISPLAY value
// ("(555) 555-0123"); validation runs on the stripped national digits.
// US/CA only (the Clerk SMS allowlist), both +1 — see src/lib/phone.ts.
export const phoneLoginSchema = z.object({
  phone: z
    .string()
    .min(1, 'Please enter your phone number')
    .refine((value) => isCompleteNanpPhone(stripPhoneDigits(value)), {
      message: 'Please enter a valid 10-digit phone number',
    }),
});

export const signupEmailSchema = z.object({
  email: emailSchema,
});

export const forgotPasswordSchema = z.object({
  email: emailSchema,
});

export const resetPasswordSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

function validateAge(month: number, day: number, year: number): { valid: boolean; error: string | null } {
  const date = new Date(year, month - 1, day);
  if (date.getMonth() !== month - 1 || date.getDate() !== day || date.getFullYear() !== year) {
    return { valid: false, error: 'Please enter a valid date.' };
  }
  const today = new Date();
  let age = today.getFullYear() - year;
  const monthDiff = today.getMonth() - (month - 1);
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < day)) {
    age--;
  }
  if (age < 18) return { valid: false, error: 'You must be at least 18 years old to sign up.' };
  if (age > 85) return { valid: false, error: 'Please enter a valid date of birth.' };
  return { valid: true, error: null };
}

export const onboardingSchema = z
  .object({
    email: emailSchema,
    firstName: nameAnswerSchema,
    lastName: nameAnswerSchema,
    dobMonth: z.coerce.number().int().min(1).max(12),
    dobDay: z.coerce.number().int().min(1).max(31),
    dobYear: z.coerce.number().int().min(1900).max(new Date().getFullYear()),
    ageConfirmed: z.literal(true, { errorMap: () => ({ message: 'You must confirm your age' }) }),
  })
  .superRefine((data, ctx) => {
    const result = validateAge(data.dobMonth, data.dobDay, data.dobYear);
    if (!result.valid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: result.error ?? 'Invalid date of birth',
        path: ['dobYear'],
      });
    }
  });

export type LoginInput = z.infer<typeof loginSchema>;
export type OTPInput = z.infer<typeof otpSchema>;
export type PhoneLoginInput = z.infer<typeof phoneLoginSchema>;
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;
export type OnboardingInput = z.infer<typeof onboardingSchema>;
